import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, fmt, fmtPiles, fmtDateTime, formatApiError } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, AlertTriangle, PackageX, Droplets, PackageCheck, Sprout, TriangleAlert, BookmarkPlus, Trash2, FileText } from "lucide-react";

const LEVEL_STYLE = {
  red: "border-red-300 bg-red-50 text-red-800",
  orange: "border-amber-300 bg-amber-50 text-amber-800",
  blue: "border-blue-300 bg-blue-50 text-blue-800",
};

function PileList({ rows, tone }) {
  const color = tone === "red" ? "text-red-700" : tone === "orange" ? "text-amber-700" : tone === "green" ? "text-emerald-700" : "text-slate-800";
  return (
    <div className="grid grid-cols-3 gap-2">
      {rows.map((r) => (
        <div key={r.type} className="rounded-lg bg-white/70 border border-slate-200 p-2 text-center">
          <div className={`font-data font-black leading-tight ${String(r.display || "").includes("+") ? "text-base" : "text-2xl"} ${color}`}>{r.display ?? fmt(r.piles)}</div>
          <div className="text-[11px] text-slate-500 leading-tight mt-0.5">{r.label}</div>
        </div>
      ))}
    </div>
  );
}

function Section({ title, icon: Icon, iconClass, children, testid }) {
  return (
    <div className="rounded-2xl border-2 border-slate-200 bg-white p-4 shadow-sm" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className={`h-5 w-5 ${iconClass}`} strokeWidth={2.3} />
        <h2 className="font-display font-extrabold uppercase tracking-tight text-slate-900">{title}</h2>
      </div>
      {children}
    </div>
  );
}

function ReservationsPanel({ fermes, types }) {
  const qc = useQueryClient();
  const [dest, setDest] = useState("");
  const [qty, setQty] = useState({});
  const [saving, setSaving] = useState(false);
  const { data: resas = [] } = useQuery({
    queryKey: ["reservations"],
    queryFn: async () => (await api.get("/reservations")).data,
  });
  const psOf = (code) => ((types || []).find((t) => t.code === code) || {}).pile_size || 1;
  const refresh = () => { qc.invalidateQueries({ queryKey: ["reservations"] }); qc.invalidateQueries({ queryKey: ["logistics-overview"] }); };

  const submit = async () => {
    if (!dest) { toast.error("Choisissez une ferme destinataire."); return; }
    const lines = (types || []).map((t) => ({ packaging_type: t.code, piles: Number(qty[t.code]) || 0 })).filter((l) => l.piles > 0);
    if (lines.length === 0) { toast.error("Indiquez au moins une quantité."); return; }
    setSaving(true);
    try {
      await api.post("/reservations", { site: dest, lines });
      toast.success("Réservation enregistrée.");
      setQty({}); setDest(""); refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };
  const cancel = async (id) => {
    try { await api.delete(`/reservations/${id}`); toast.success("Réservation annulée."); refresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  return (
    <div className="rounded-2xl border-2 border-slate-200 bg-white p-4" data-testid="reservations-panel">
      <div className="flex items-center gap-2 mb-3">
        <BookmarkPlus className="h-5 w-5 text-blue-600" strokeWidth={2.3} />
        <h2 className="font-display font-extrabold uppercase tracking-tight text-slate-900">Réserver des propres pour une ferme</h2>
      </div>
      <div className="grid sm:grid-cols-4 gap-2 items-end">
        <div className="sm:col-span-1">
          <Label className="text-xs">Ferme</Label>
          <Select value={dest} onValueChange={setDest}>
            <SelectTrigger className="h-11" data-testid="resa-ferme"><SelectValue placeholder="Choisir…" /></SelectTrigger>
            <SelectContent>
              {(fermes || []).map((f) => <SelectItem key={f.site} value={f.site}>{f.site_name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {(types || []).map((t) => (
          <div key={t.code}>
            <Label className="text-xs">{t.label} (piles)</Label>
            <Input type="number" min="0" value={qty[t.code] || ""} placeholder="0"
              onChange={(e) => setQty((p) => ({ ...p, [t.code]: e.target.value }))}
              className="h-11 font-data" data-testid={`resa-qty-${t.code}`} />
          </div>
        ))}
      </div>
      <Button onClick={submit} disabled={saving} className="mt-3 h-11 bg-blue-800 hover:bg-blue-900 font-bold" data-testid="submit-reservation-button">
        {saving ? "Enregistrement…" : "Réserver"}
      </Button>

      <div className="mt-4 divide-y divide-slate-100 border-t border-slate-100">
        {resas.length === 0 && <p className="text-sm text-slate-500 pt-3">Aucune réservation active.</p>}
        {resas.map((r) => (
          <div key={r.id} className="py-2 flex items-center gap-3" data-testid={`reservation-${r.id}`}>
            <span className="font-display font-bold text-slate-800 min-w-[110px]">{r.site_name}</span>
            <span className="text-sm font-data text-slate-600 flex-1">
              {(r.lines || []).map((l) => `${l.packaging_type}: ${fmtPiles(l.units, psOf(l.packaging_type), true)}`).join(" · ")}
            </span>
            <Button size="icon" variant="ghost" className="h-9 w-9 text-red-600" onClick={() => cancel(r.id)} data-testid={`cancel-reservation-${r.id}`} title="Annuler la réservation" aria-label="Annuler la réservation">
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function PilotageLogistique() {
  const { can } = useAuth();
  const { data, isLoading } = useQuery({
    queryKey: ["logistics-overview"],
    queryFn: async () => (await api.get("/logistics/overview")).data,
    enabled: can("logistics", "view"),
    refetchInterval: 30000,
  });


  const months = useMemo(() => {
    const out = [];
    const now = new Date();
    for (let i = 0; i < 12; i += 1) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      out.push({ value, label: d.toLocaleDateString("fr-FR", { month: "long", year: "numeric" }) });
    }
    return out;
  }, []);
  const [month, setMonth] = useState(months[0].value);
  const exportPertes = async () => {
    try {
      const res = await api.get("/export/pertes.pdf", { params: { month }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `rapport_pertes_${month}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const exportPdf = async () => {
    try {
      const res = await api.get("/export/logistics.pdf", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "pilotage_logistique.pdf"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const alerts = data?.alerts || [];

  if (!can("logistics", "view")) return <Navigate to="/" replace />;
  return (
    <AppShell>
      <div className="space-y-5" data-testid="pilotage-page">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="font-display font-black text-2xl sm:text-3xl text-slate-900 uppercase tracking-tight">Pilotage logistique emballages</h1>
            <p className="text-sm text-slate-500">Vision centralisée ANNEZIN ↔ DDO ↔ Fermes — quantités en piles.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" className="h-11 border-2 tactile" onClick={exportPdf} data-testid="export-logistics-pdf-button">
              <FileText className="h-4 w-4 mr-2" /> Export PDF
            </Button>
            <div className="flex items-center gap-1 rounded-lg border-2 border-red-200 bg-red-50 p-1" data-testid="pertes-report">
              <Select value={month} onValueChange={setMonth}>
                <SelectTrigger className="h-9 w-44 bg-white capitalize" data-testid="pertes-report-month"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {months.map((m) => <SelectItem key={m.value} value={m.value} className="capitalize" data-testid={`pertes-month-${m.value}`}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button className="h-9 bg-red-700 hover:bg-red-800 text-white font-bold" onClick={exportPertes} data-testid="export-pertes-pdf-button">
                <PackageX className="h-4 w-4 mr-1.5" /> Rapport des pertes
              </Button>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border-2 border-slate-200 bg-white p-4" data-testid="logistics-alerts">
          <div className="flex items-center gap-2 mb-3">
            <TriangleAlert className="h-5 w-5 text-amber-600" strokeWidth={2.3} />
            <h2 className="font-display font-extrabold uppercase tracking-tight text-slate-900">Alertes</h2>
            <span className="ml-auto text-xs font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{alerts.length}</span>
          </div>
          {alerts.length === 0 ? (
            <p className="text-sm text-emerald-700 font-semibold">✅ Aucune alerte — tout est sous contrôle.</p>
          ) : (
            <div className="space-y-2">
              {alerts.map((a, i) => (
                <div key={i} className={`rounded-lg border-2 p-2.5 text-sm font-semibold flex items-start gap-2 ${LEVEL_STYLE[a.level] || LEVEL_STYLE.blue}`} data-testid={`alert-${a.code}-${i}`}>
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>{a.message}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {data && (
          <div className="rounded-2xl border-2 border-red-200 bg-white p-4" data-testid="pertes-transport">
            <div className="flex items-center gap-2 mb-3">
              <PackageX className="h-5 w-5 text-red-600" strokeWidth={2.3} />
              <h2 className="font-display font-extrabold uppercase tracking-tight text-slate-900">Pertes transport <span className="text-xs font-semibold normal-case text-slate-500">(envoyé − reçu, 30 derniers jours)</span></h2>
              <span className="ml-auto text-xs font-bold px-2 py-0.5 rounded-full bg-red-50 text-red-700">{(data.pertes_transport || []).length}</span>
            </div>
            {(data.pertes_transport || []).length === 0 ? (
              <p className="text-sm text-emerald-700 font-semibold">Aucune perte transport.</p>
            ) : (
              <div className="divide-y divide-slate-100">
                {data.pertes_transport.map((p) => (
                  <div key={p.id} className="py-2 text-sm" data-testid={`perte-transport-${p.id}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-slate-800">{p.from_name} → {p.to_name}</span>
                      <span className="text-xs font-data text-slate-400 ml-auto">{fmtDateTime(p.date)}</span>
                    </div>
                    <div className="font-data text-red-700">{p.lines.map((l) => `${l.label} : ${l.display}`).join(" · ")}</div>
                    {p.commentaire && <div className="text-xs text-slate-500 italic">« {p.commentaire} »</div>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {isLoading && (
          <div className="flex items-center gap-2 text-slate-500 text-sm" data-testid="pilotage-loading">
            <Loader2 className="h-5 w-5 animate-spin text-blue-700" /> Chargement des données…
          </div>
        )}

        {data && (
          <>
            <Section title="ANNEZIN — Sales à expédier vers DDO" icon={PackageX} iconClass="text-red-600" testid="section-annezin">
              <PileList rows={data.annezin.sales} tone="red" />
            </Section>

            <div className="grid sm:grid-cols-2 gap-4">
              <Section title="DDO — En attente de lavage" icon={Droplets} iconClass="text-amber-600" testid="section-ddo-sales">
                <PileList rows={data.ddo.sales_attente} tone="orange" />
              </Section>
              <Section title="DDO — En cours de lavage" icon={Droplets} iconClass="text-blue-600" testid="section-ddo-enlavage">
                <PileList rows={data.ddo.en_lavage} tone="blue" />
              </Section>
              <Section title="DDO — Propres (total / réservé)" icon={PackageCheck} iconClass="text-emerald-600" testid="section-ddo-propres">
                <PileList rows={data.ddo.propres} tone="green" />
                <p className="text-[11px] font-bold uppercase text-slate-400 mt-2 mb-1">Dont réservé</p>
                <PileList rows={data.ddo.reserved} tone="blue" />
              </Section>
              <Section title="DDO — Disponible (non réservé)" icon={PackageCheck} iconClass="text-emerald-700" testid="section-ddo-dispo">
                <PileList rows={data.ddo.disponibles} tone="green" />
              </Section>
            </div>

            {can("reservation", "create") && (
              <ReservationsPanel fermes={data.fermes} types={data.types} dispo={data.ddo.disponibles} />
            )}

            <Section title="Fermes — Stock, besoins & alertes" icon={Sprout} iconClass="text-blue-600" testid="section-fermes">
              <div className="space-y-3">
                {data.fermes.length === 0 && <p className="text-sm text-slate-500">Aucune ferme.</p>}
                {data.fermes.map((f) => (
                  <div key={f.site} className={`rounded-xl border-2 p-3 ${f.low_flag ? "border-red-300 bg-red-50/60" : "border-slate-200"}`} data-testid={`ferme-card-${f.site}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-display font-bold text-slate-900">{f.site_name}</span>
                      {f.low_flag
                        ? <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700">🔴 Stock faible</span>
                        : <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">🟢 OK</span>}
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                      <div>
                        <p className="text-[11px] font-bold uppercase text-slate-400 mb-1">Stock actuel (propre)</p>
                        <PileList rows={f.stock} tone="green" />
                      </div>
                      <div>
                        <p className="text-[11px] font-bold uppercase text-slate-400 mb-1" data-testid={`besoin-label-${f.site}`}>{f.has_besoin ? "Besoin déclaré" : "Aucun besoin déclaré"}</p>
                        <PileList rows={f.besoin} tone="blue" />
                      </div>
                      <div>
                        <p className="text-[11px] font-bold uppercase text-slate-400 mb-1">Réservé chez DDO</p>
                        <PileList rows={f.reserved || []} tone="blue" />
                      </div>
                      <div className={`rounded-lg p-1.5 -m-1.5 ${f.has_envoi ? "bg-amber-50 ring-2 ring-amber-300" : ""}`} data-testid={`a-envoyer-${f.site}`}>
                        <p className="text-[11px] font-bold uppercase text-amber-700 mb-1">À envoyer (besoin − réservé)</p>
                        <PileList rows={f.a_envoyer || []} tone="orange" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          </>
        )}
      </div>
    </AppShell>
  );
}
